import React, { useState, useContext, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Card,
  CardHeader,
  IconButton,
  CardContent,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  Button,
  MenuItem,
  Pagination,
  CircularProgress,
} from '@mui/material';
import { NumericFormat } from 'react-number-format';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import { DatePicker } from '@mui/x-date-pickers';
import { toast } from 'react-toastify';
import { styled } from '@mui/material/styles';
import Swal from 'sweetalert2';
import moment from 'moment';
import { numberFormat } from '../../../helpers/helperFunctions';
// Material Icon
import {
  Person,
  ArrowDropDown,
  People,
  Close,
  Assignment,
  AccessTime,
  Payments,
  PictureInPicture,
  Add,
} from '@mui/icons-material';

// Context
import useCcoContext from '../context/useCcoContext';
// Components
import EngagementOptionModal from './EngagementOptionModal';
import CollectionStatusOptionModa from './CollectionStatusOptionModa';
import AttachmentStepper from './AttachmentStepper';
import AttachmentUploadModal from './AttachmentUploadModal';
// API
import {
  getMoeList,
  getCollectionStatusList,
  postClienCallOutStore,
  getClientCalloutLatest,
  getClientCalloutField,
} from '../../../api/ccoApi';

// ---- Theme colors -------------------------------------------------------
const DARK_GREEN = '#567033ff';
const GREEN = '#6B8D3E';
const PEACH = '#f1d0bdff';

const CardContentNoPadding = styled(CardContent)(`
  padding: 0;
  &:last-child {
    padding-bottom: 0;
  }
`);

// ---- Small presentational helpers --------------------------------------

// Full-width dark-green column heading (Tele / Acknowledgement / Field Engagement)
function ColumnTitle({ children }) {
  return (
    <Box
      sx={{
        backgroundColor: DARK_GREEN,
        height: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography variant='button' sx={{ color: '#fff' }}>
        {children}
      </Typography>
    </Box>
  );
}

// Green sub-panel header with optional avatar / action button.
// Fixed height so every header bar lines up across columns.
function PanelHeader({ icon, title, action }) {
  return (
    <CardHeader
      sx={{
        backgroundColor: GREEN,
        color: '#fff',
        height: 52,
        py: 0,
        '& .MuiCardHeader-action': { alignSelf: 'center', mt: 0, mb: 0 },
      }}
      avatar={icon}
      action={action}
      title={
        <Typography variant='button' component='h6'>
          {title}
        </Typography>
      }
      align='center'
    />
  );
}

// Peach label band used above inputs
function PeachBar({ children }) {
  return (
    <Box sx={{ display: 'flex', backgroundColor: PEACH, color: '#000', fontWeight: 900, p: 2 }}>
      {children}
    </Box>
  );
}

// Account-info label / value pair
function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex' }}>
      <Box sx={{ width: '35%', backgroundColor: PEACH, p: 1 }}>
        <Typography variant='button' sx={{ fontWeight: 'bold' }}>
          {label}
        </Typography>
      </Box>
      <Box sx={{ width: '65%', p: 1 }}>
        <Typography variant='button'>{value}</Typography>
      </Box>
    </Box>
  );
}

// Red asterisk marking a required field
function Required() {
  return (
    <Box component='span' sx={{ color: '#d32f2f', ml: 0.5 }}>
      *
    </Box>
  );
}

// Single "Label: value" line used in the Field Engagement panels
function InfoLine({ label, value }) {
  return (
    <Typography component='div' sx={{ fontSize: '20px' }}>
      {label}: {value}
    </Typography>
  );
}

// Captioned colored rating tile used in the Credit Rating panel
function RatingBox({ label, rating, description, bgColor }) {
  const hasRating = rating !== null && rating !== undefined && rating !== '';

  return (
    <Box pt={2}>
      <Box sx={{ backgroundColor: hasRating ? bgColor : undefined, p: 3, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '32px', fontWeight: 'bold' }}>
          {hasRating ? rating : '-'}
        </Typography>
        <Typography sx={{ fontSize: '16px' }}>{description}</Typography>
      </Box>
    </Box>
  );
}

// ---- Latest Engagement filter -------------------------------------------
const ENGAGEMENT_FILTER_OPTIONS = [
  { value: 'last', label: 'Last Engagement' },
  { value: 'three-months', label: 'Last 3 Months' },
  { value: 'six-months', label: 'Last 6 Months' },
  { value: 'custom', label: 'Custom, Date Range' },
];

const DEFAULT_ENGAGEMENT_FILTER = { type: null, date_from: null, date_to: null };

// Oldest selectable date for the custom range
const ENGAGEMENT_MIN_DATE = moment().subtract(3, 'years').startOf('day');

// Dropdown + optional custom date range shown below the Field / Admin sub headers
function EngagementFilter({ filter, setFilter }) {
  const handleChangeType = (event) => {
    setFilter({ ...DEFAULT_ENGAGEMENT_FILTER, type: event.target.value });
  };

  const handleChangeRange = (value, name) => {
    setFilter({ ...filter, [name]: value ? moment(value).format('YYYY-MM-DD') : null });
  };

  return (
    <Box sx={{ p: 2, pb: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <TextField
        select
        size='small'
        fullWidth
        label='Filter'
        value={filter?.type ?? ''}
        onChange={handleChangeType}
      >
        {ENGAGEMENT_FILTER_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      {filter?.type === 'custom' ? (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <DatePicker
            label='From'
            views={['year', 'month', 'day']}
            value={filter?.date_from}
            minDate={ENGAGEMENT_MIN_DATE}
            maxDate={filter?.date_to ? moment(filter.date_to) : moment()}
            onChange={(value) => handleChangeRange(value, 'date_from')}
            // eslint-disable-next-line react/jsx-props-no-spreading
            renderInput={(params) => <TextField {...params} size='small' fullWidth />}
          />
          <DatePicker
            label='To'
            views={['year', 'month', 'day']}
            value={filter?.date_to}
            minDate={filter?.date_from ? moment(filter.date_from) : ENGAGEMENT_MIN_DATE}
            maxDate={moment()}
            onChange={(value) => handleChangeRange(value, 'date_to')}
            // eslint-disable-next-line react/jsx-props-no-spreading
            renderInput={(params) => <TextField {...params} size='small' fullWidth />}
          />
        </Box>
      ) : null}
    </Box>
  );
}

// No type (null) is a valid request; a custom range only fires once both ends are picked
const isEngagementFilterReady = (filter) => {
  if (filter?.type === 'custom') return Boolean(filter.date_from && filter.date_to);
  return true;
};

const formatEngagementDate = (value) => (value ? moment(value).format('LL') : '-');

// API may return a single record or a list — always work with a list
const toEngagementList = (data) => {
  if (Array.isArray(data)) return data;
  return data ? [data] : [];
};

// Paginated engagement records — one record per page
function EngagementList({ items, renderItem, loading }) {
  const [page, setPage] = useState(1);

  // Back to the first page whenever a new result set arrives
  useEffect(() => {
    setPage(1);
  }, [items]);

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography color='text.secondary'>Loading engagement...</Typography>
      </Box>
    );
  }

  if (!items?.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color='text.secondary'>No engagement found.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {renderItem(items[page - 1])}
      </Box>
      {items.length > 1 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pb: 2 }}>
          <Pagination
            size='small'
            count={items.length}
            page={page}
            onChange={(event, value) => setPage(value)}
          />
        </Box>
      ) : null}
    </Box>
  );
}

export default function ViewClientCallOutModalv2() {
  const { viewCco, setViewCco, handleGenerateFilter } = useContext(useCcoContext);
  const defaultForm = viewCco.row;

  const [form, setForm] = useState(defaultForm);
  const [engagementOptions, setEngagementOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  // MODAL
  const [enagementModal, setEngagementModal] = useState(false);
  const [statusModal, setStatusModal] = useState(false);
  const [uploadAttachmentModal, setUploadAttachmentModal] = useState(false);

  // ATTACHMENT
  const [loadingAttachment, setLoadingAttachment] = useState(false);

  // LATEST ENGAGEMENT — seeded with the row's data, then refreshed from the API (default: 'last')
  const [foeFilter, setFoeFilter] = useState(DEFAULT_ENGAGEMENT_FILTER);
  const [cooFilter, setCooFilter] = useState(DEFAULT_ENGAGEMENT_FILTER);
  const [foeData, setFoeData] = useState([]);
  const [cooData, setCooData] = useState([]);
  const [loadingFoe, setLoadingFoe] = useState(false);
  const [loadingCoo, setLoadingCoo] = useState(false);

  // A field is locked (read-only) when it already had a value when the modal opened.
  // Uses the original row (defaultForm), not the live form state, so editing never
  // re-locks a field mid-typing.
  const hasInitialValue = (name) => {
    const v = defaultForm?.[name];
    return v !== null && v !== undefined && v !== '';
  };

  // Attachments are locked once the record is submitted or already has uploaded proof.
  const hasInitialAttachments = defaultForm?.attachments?.length > 0;
  const isAttachmentLocked = hasInitialValue('id') || hasInitialAttachments;

  const creditRatingBgColor = (value) => {
    let bgValue;

    if (Math.floor(value) === 1) {
      bgValue = '#d32f2f';
    }

    if (Math.floor(value) === 2) {
      bgValue = '#ff9800';
    }

    if (Math.floor(value) === 3) {
      bgValue = '#fbff00fd';
    }

    if (Math.floor(value) === 4) {
      bgValue = '#4caf50';
    }

    if (Math.floor(value) === 5) {
      bgValue = '#2e7d32';
    }

    return bgValue;
  };

  const handleClose = () => {
    setViewCco({ ...viewCco, toggle: false, row: {} });
  };

  const handleMoeList = async () => {
    const response = await getMoeList();
    const { code, data } = response.data;

    if (code === '200') {
      setEngagementOptions(data);
    }
  };

  const handleChangeMoe = async (row) => {
    setForm({ ...form, moe_id: row?.id });
    setEngagementModal(false);
  };

  const handleCollectionStatusList = async () => {
    const response = await getCollectionStatusList();
    const { code, data } = response.data;

    if (code === '200') {
      setStatusOptions(data);
    }
  };

  const handleChangeCollectionStatus = async (row) => {
    setForm({ ...form, collection_status_id: row?.id });
    setStatusModal(false);
  };

  const handleChange = (event, name) => {
    setForm({ ...form, [name]: event?.target?.value });
  };

  const handleChangeDate = (value, name) => {
    setForm({ ...form, [name]: moment(value).format('YYYY-MM-DD') });
  };

  const handleChangeTime = (value, name) => {
    setForm({ ...form, [name]: value });
  };

  const handleSubmitClientCallOut = async () => {
    // Proof of call outs is required before submission
    if (!isAttachmentLocked && !form?.attachments_to_upload?.length) {
      Swal.fire({
        title: 'Invalid Request',
        text: 'Proof of Call Outs is required. Upload at least one file.',
        icon: 'warning',
      });
      return;
    }

    try {
      const request = {
        account_code: form?.account_code,
        id: form?.id ?? null,
        collection_status_id: form?.collection_status_id,
        moe_id: form?.moe_id,
        date_schedule: form?.date_schedule,
        time_ended: moment(form?.time_ended).format('YYYY-MM-DD HH:mm:ss'),
        time_started: moment(form?.time_started).format('YYYY-MM-DD HH:mm:ss'),
        collection_amount: form?.collection_amount,
        discussion_remarks: form?.discussion_remarks,
        issues_concerns: form?.issues_concerns,
        attachments: form?.attachments_to_upload,
      };

      const response = await postClienCallOutStore(request);
      const { code, message } = response?.data;

      if (code === '00003' || code === '00004') {
        toast.success(message);
        handleClose();
        handleGenerateFilter();
      }

      if (response?.data?.response?.code === '500') {
        Swal.fire({
          title: 'Invalid Request',
          html: response.data.response.result.join('<br />'),
          icon: 'warning',
        });
        toast.dismiss();
      }
    } catch (error) {
      Swal.fire({
        title: 'Something went wrong',
        text: error?.message ?? 'Unable to submit client call out. Please try again.',
        icon: 'error',
      });
      toast.dismiss();
    }
  };

  const handleLatestEngagement = async (apiCall, filter, setData, setLoading) => {
    setLoading(true);
    try {
      const request = {
        account_code: form?.account_code,
        type: filter?.type ?? null,
        date_as_of: form.date_schedule,
        date_from: filter?.date_from ?? null,
        date_to: filter?.date_to ?? null,
      };
      const response = await apiCall(request);
      const { code, data } = response.data;

      if (code === '200') {
        setData(toEngagementList(data));
      }
    } catch (error) {
      toast.error(error?.message ?? 'Unable to load latest engagement.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleMoeList();
    handleCollectionStatusList();
  }, []);

  useEffect(() => {
    if (isEngagementFilterReady(foeFilter)) {
      handleLatestEngagement(getClientCalloutField, foeFilter, setFoeData, setLoadingFoe);
    }
  }, [foeFilter, form.date_schedule]);

  useEffect(() => {
    if (isEngagementFilterReady(cooFilter)) {
      handleLatestEngagement(getClientCalloutLatest, cooFilter, setCooData, setLoadingCoo);
    }
  }, [cooFilter, form.date_schedule]);

  return (
    <Dialog fullScreen open={viewCco?.toggle} onClose={handleClose}>
      <DialogTitle>
        {form?.account_code} - {form?.customer_name}
        <IconButton
          aria-label='close'
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Submit — hidden for existing records (already saved) */}
        {hasInitialValue('id') ? null : (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }} mb={2}>
            <Button variant='contained' color='success' onClick={handleSubmitClientCallOut}>
              Submit
            </Button>
          </Box>
        )}

        {/* ACCOUNT INFORMATION */}
        <Card>
          <PanelHeader icon={<Person />} title='Account Information' />
          <CardContentNoPadding>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              }}
            >
              <Box>
                <InfoRow label='Name' value={form?.customer_name} />
                <InfoRow label='Address' value={form?.address} />
              </Box>
              <Box>
                <InfoRow label='Contact No' value={form?.customer_contact_no} />
                <InfoRow label='Email' value={form?.customer_email} />
                <InfoRow label='Account Type/Channel' value={form?.customer_type} />
              </Box>
              <Box>
                <InfoRow label='Payment Mode' value={form?.payment_mode} />
                <InfoRow label='Payment Terms' value={form?.payment_terms} />
              </Box>
            </Box>
          </CardContentNoPadding>
        </Card>

        {/* THREE MAIN COLUMNS */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            mt: 2,
          }}
        >
          {/* ---------- TELE - COLLECTION LOG ---------- */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <ColumnTitle>Tele - Collection Log</ColumnTitle>

            {/* Mode of engagement + Status */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
              <Card sx={{ borderRadius: 0, flex: 1 }}>
                <PanelHeader
                  icon={<People />}
                  title={
                    <>
                      Mode Of Engagement
                      {hasInitialValue('moe_id') ? null : <Required />}
                    </>
                  }
                  action={
                    hasInitialValue('moe_id') ? null : (
                      <IconButton
                        aria-label='mode of engagement'
                        sx={{ color: '#fff' }}
                        onClick={() => setEngagementModal(true)}
                      >
                        <ArrowDropDown />
                      </IconButton>
                    )
                  }
                />
                <CardContentNoPadding>
                  <List>
                    <ListItem>
                      <ListItemText>
                        {engagementOptions.find((row) => row?.id === form?.moe_id)?.description}
                      </ListItemText>
                    </ListItem>
                  </List>
                </CardContentNoPadding>
              </Card>

              <Card sx={{ borderRadius: 0, flex: 1 }}>
                <PanelHeader
                  icon={<Assignment />}
                  title={
                    <>
                      Status
                      {hasInitialValue('collection_status_id') ? null : <Required />}
                    </>
                  }
                  action={
                    hasInitialValue('collection_status_id') ? null : (
                      <IconButton
                        aria-label='status'
                        sx={{ color: '#fff' }}
                        onClick={() => setStatusModal(true)}
                      >
                        <ArrowDropDown />
                      </IconButton>
                    )
                  }
                />
                <CardContentNoPadding>
                  <List>
                    <ListItem>
                      <ListItemText>
                        {
                          statusOptions.find((row) => row?.id === form?.collection_status_id)
                            ?.description
                        }
                      </ListItemText>
                    </ListItem>
                  </List>
                </CardContentNoPadding>
              </Card>
            </Box>

            {/* Objective / Touch point discussed */}
            <Card sx={{ borderRadius: 0, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <PanelHeader title='Objective/Touch Point Discussed' />
              <CardContentNoPadding sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <PeachBar>
                  <Box sx={{ flex: 1 }}>
                    Discussion/Remarks
                    {hasInitialValue('discussion_remarks') ? null : <Required />}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    Issue/Concern
                    {hasInitialValue('issues_concerns') ? null : <Required />}
                  </Box>
                </PeachBar>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    flexGrow: 1,
                  }}
                >
                  <TextField
                    sx={{
                      flex: 1,
                      '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' },
                    }}
                    variant='outlined'
                    onChange={(e) => handleChange(e, 'discussion_remarks')}
                    name='discussion_remarks'
                    fullWidth
                    multiline
                    minRows={16}
                    value={form?.discussion_remarks}
                    InputProps={{ readOnly: hasInitialValue('discussion_remarks') }}
                  />
                  <TextField
                    sx={{
                      flex: 1,
                      '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' },
                    }}
                    variant='outlined'
                    onChange={(e) => handleChange(e, 'issues_concerns')}
                    name='issues_concerns'
                    fullWidth
                    multiline
                    minRows={16}
                    value={form?.issues_concerns}
                    InputProps={{ readOnly: hasInitialValue('issues_concerns') }}
                  />
                </Box>
              </CardContentNoPadding>
            </Card>
          </Box>

          {/* ---------- ACKNOWLEDGEMENT ---------- */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <ColumnTitle>Acknowledgement</ColumnTitle>

            {/* Date */}
            <Card sx={{ borderRadius: 0 }}>
              <PanelHeader icon={<AccessTime />} title='Date' />
              <CardContentNoPadding>
                <PeachBar>
                  Date Schedule
                  {hasInitialValue('date_schedule') ? null : <Required />}
                </PeachBar>
                <DatePicker
                  views={['month', 'day', 'year']}
                  value={form?.date_schedule}
                  readOnly={hasInitialValue('date_schedule')}
                  renderInput={(params) => (
                    <TextField
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...params}
                      size='small'
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: hasInitialValue('date_schedule')
                          ? null
                          : params.InputProps?.endAdornment,
                      }}
                    />
                  )}
                  onChange={(value) => handleChangeDate(value, 'date_schedule')}
                />
              </CardContentNoPadding>
            </Card>

            {/* Time */}
            <Card sx={{ borderRadius: 0 }}>
              <PanelHeader icon={<AccessTime />} title='Time' />
              <CardContentNoPadding>
                <PeachBar>
                  <Box sx={{ flex: 1 }}>
                    Time Started
                    {hasInitialValue('time_started') ? null : <Required />}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    Time Ended
                    {hasInitialValue('time_ended') ? null : <Required />}
                  </Box>
                </PeachBar>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box sx={{ flex: 1 }}>
                    <DesktopTimePicker
                      renderInput={(params) => (
                        <TextField
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...params}
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: hasInitialValue('time_started') ? null : (
                              <>
                                <Button
                                  size='small'
                                  onClick={() => handleChangeTime(moment(), 'time_started')}
                                >
                                  Now
                                </Button>
                                {params.InputProps?.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      value={moment(form?.time_started)}
                      readOnly={hasInitialValue('time_started')}
                      onChange={(newValue) => handleChangeTime(newValue, 'time_started')}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <DesktopTimePicker
                      renderInput={(params) => (
                        <TextField
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...params}
                          fullWidth
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: hasInitialValue('time_ended') ? null : (
                              <>
                                <Button
                                  size='small'
                                  onClick={() => handleChangeTime(moment(), 'time_ended')}
                                >
                                  Now
                                </Button>
                                {params.InputProps?.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      value={moment(form?.time_ended)}
                      readOnly={hasInitialValue('time_ended')}
                      onChange={(newValue) => handleChangeTime(newValue, 'time_ended')}
                    />
                  </Box>
                </Box>
              </CardContentNoPadding>
            </Card>

            {/* Estimated amount */}
            <Card sx={{ borderRadius: 0 }}>
              <PanelHeader
                icon={<Payments />}
                title={
                  <>
                    Estimated Amount for Collection
                    {hasInitialValue('collection_amount') ? null : <Required />}
                  </>
                }
              />
              <CardContentNoPadding>
                <NumericFormat
                  value={form?.collection_amount ?? 0}
                  prefix='₱'
                  thousandSeparator
                  customInput={TextField}
                  fullWidth
                  readOnly={hasInitialValue('collection_amount')}
                  InputProps={{ readOnly: hasInitialValue('collection_amount') }}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      collection_amount: parseFloat(e.target.value?.replace(/₱|,/g, '')),
                    })
                  }
                />
              </CardContentNoPadding>
            </Card>

            {/* Proof of call outs */}
            <Card sx={{ borderRadius: 0, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <PanelHeader
                icon={<PictureInPicture />}
                title={
                  <>
                    Proof of Call Outs
                    {isAttachmentLocked ? null : <Required />}
                  </>
                }
                action={
                  isAttachmentLocked ? null : (
                    <IconButton
                      aria-label='add attachment'
                      sx={{ color: '#fff' }}
                      onClick={() => setUploadAttachmentModal(true)}
                    >
                      <Add />
                    </IconButton>
                  )
                }
              />
              <CardContent sx={{ flexGrow: 1, minHeight: 200 }}>
                {form?.attachments_to_upload ? (
                  <>
                    {form?.attachments_to_upload.length} file(s) pending to upload
                    <br />
                    <br />
                  </>
                ) : (
                  ''
                )}

                {form?.attachments?.length > 0 ? (
                  <AttachmentStepper
                    attachment={form?.attachments}
                    loadingAttachment={loadingAttachment}
                    setLoadingAttachment={setLoadingAttachment}
                    readOnly={isAttachmentLocked}
                  />
                ) : (
                  'No Attachment'
                )}
              </CardContent>
            </Card>
          </Box>

          {/* ---------- FIELD ENGAGEMENT ---------- */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <ColumnTitle>Latest Engagement</ColumnTitle>

            {/* Latest FoE + Last CCO */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
              <Card sx={{ borderRadius: 0, flex: 1 }}>
                <PanelHeader title='Field' />
                <CardContentNoPadding>
                  <EngagementFilter filter={foeFilter} setFilter={setFoeFilter} />
                  <EngagementList
                    items={foeData}
                    loading={loadingFoe}
                    renderItem={(row) => (
                      <>
                        <InfoLine label='Date' value={formatEngagementDate(row?.date_schedule)} />
                        <InfoLine label='Status' value={row?.status ?? '-'} />
                        <InfoLine label='Mode of Engagement' value={row?.moe ?? '-'} />
                        {/* Row data uses `commitement_notes`; the endpoint returns `commitment_notes` */}
                        <InfoLine
                          label='Commitment Notes'
                          value={row?.commitement_notes ?? row?.commitment_notes ?? '-'}
                        />
                      </>
                    )}
                  />
                </CardContentNoPadding>
              </Card>

              <Card sx={{ borderRadius: 0, flex: 1 }}>
                <PanelHeader title='Admin' />
                <CardContentNoPadding>
                  <EngagementFilter filter={cooFilter} setFilter={setCooFilter} />
                  <EngagementList
                    items={cooData}
                    loading={loadingCoo}
                    renderItem={(row) => (
                      <>
                        <InfoLine label='Date' value={formatEngagementDate(row?.date_schedule)} />
                        <InfoLine label='Status' value={row?.collection_status ?? '-'} />
                        <InfoLine label='Mode of Engagement' value={row?.moe ?? '-'} />
                        <InfoLine
                          label='Discussion/Remarks'
                          value={row?.discussion_remarks ?? '-'}
                        />
                      </>
                    )}
                  />
                </CardContentNoPadding>
              </Card>
            </Box>

            {/* Credit rating + Aging */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexGrow: 1 }}>
              <Card sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <PanelHeader title='Credit Rating' />
                <CardContentNoPadding sx={{ flexGrow: 1, display: 'flex' }}>
                  <Box
                    sx={{
                      p: 2,
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <RatingBox
                      label='Credit'
                      rating={form?.rating?.rating}
                      description={form?.rating?.rating_description}
                      bgColor={creditRatingBgColor(form?.rating?.rating)}
                    />
                    <Typography
                      sx={{ mt: 2, fontSize: '32px', fontWeight: 'bold', textAlign: 'center' }}
                    >
                      PDR(%): {form.pdr.pdr_rate}
                    </Typography>
                  </Box>
                </CardContentNoPadding>
              </Card>

              <Card sx={{ borderRadius: 0, flex: 1 }}>
                <PanelHeader title='Aging Of Accounts' />
                <CardContentNoPadding>
                  <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <InfoLine label='PAST DUE' value={form?.aging?.past_due} />
                    <InfoLine label='CURRENT' value={form?.aging?.current} />
                    <InfoLine label='TOTAL AR' value={form?.aging?.total_ar} />
                    <InfoLine label='TOTAL PDC' value={form?.aging?.pdc} />
                  </Box>
                </CardContentNoPadding>
              </Card>
            </Box>
          </Box>
        </Box>

        {/* MODALS */}
        {enagementModal === true ? (
          <EngagementOptionModal
            enagementModal={enagementModal}
            setEngagementModal={setEngagementModal}
            engagementOptions={engagementOptions}
            handleChangeMoe={handleChangeMoe}
          />
        ) : (
          ''
        )}

        {statusModal === true ? (
          <CollectionStatusOptionModa
            statusModal={statusModal}
            setStatusModal={setStatusModal}
            statusOptions={statusOptions}
            handleChangeCollectionStatus={handleChangeCollectionStatus}
          />
        ) : (
          ''
        )}

        {uploadAttachmentModal === true ? (
          <AttachmentUploadModal
            form={form}
            setForm={setForm}
            loadingAttachment={loadingAttachment}
            setLoadingAttachment={setLoadingAttachment}
            uploadAttachmentModal={uploadAttachmentModal}
            setUploadAttachmentModal={setUploadAttachmentModal}
          />
        ) : (
          ''
        )}
      </DialogContent>
    </Dialog>
  );
}
